import React, { useMemo } from 'react';

import { Box, Typography, makeStyles, useTheme } from '@material-ui/core';

import { AppCustomTable } from '../../../components';
import { AppCustomTableColumn, ChangeLog } from '../../../types';
import { formatTableDate } from '../../../utils/dates';

const useStyles = makeStyles(theme => ({
  emptyContainer: {
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  actorPrimary: {
    fontWeight: 500,
    lineHeight: 1.2,
  },
  actorSecondary: {
    display: 'block',
    marginTop: 2,
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  fieldPrimary: {
    fontWeight: 500,
    lineHeight: 1.2,
  },
  fieldSecondary: {
    display: 'block',
    marginTop: 2,
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  value: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

type ChangeHistoryRow = {
  log: ChangeLog;
  change: ChangeLog['changes'][number];
};

const getActorTitle = (actor: ChangeLog['actor']): string => {
  const metadata = (actor?.metadata || {}) as Record<string, unknown>;
  const firstName = typeof metadata.first_name === 'string' ? metadata.first_name : undefined;
  const lastName = typeof metadata.last_name === 'string' ? metadata.last_name : undefined;
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  return name || '';
};

const getNullableValue = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
};

interface Props {
  logs?: ChangeLog[];
  /**
   * Filter by "Field" column (i.e. change.name). If empty/undefined, no filtering is applied.
   */
  fields?: string[];
}

export const ChangeHistoryTable: React.FC<Props> = ({ logs = [], fields }) => {
  const classes = useStyles();
  const theme = useTheme();

  const rows: ChangeHistoryRow[] = useMemo(() => {
    const shouldFilter = !!fields?.length;
    const allowedFields = shouldFilter ? new Set(fields) : undefined;

    const out: ChangeHistoryRow[] = [];
    logs.forEach(log => {
      (log.changes || []).forEach(change => {
        if (allowedFields && !allowedFields.has(change?.name)) {
          return;
        }
        out.push({ log, change });
      });
    });
    return out;
  }, [logs, fields]);

  const columns: AppCustomTableColumn[] = useMemo(
    () => [
      {
        name: 'updated_at',
        title: 'Date / Time',
        flex: 0.18,
        width: '140px',
        renderCell: (row: ChangeHistoryRow) => (
          <Typography variant="body2">{formatTableDate(row.log.updated_at, true)}</Typography>
        ),
      },
      {
        name: 'actor',
        title: 'By who?',
        flex: 0.22,
        width: '180px',
        renderCell: (row: ChangeHistoryRow) => (
          <Box>
            <Typography variant="body2" className={classes.actorPrimary}>
              {getActorTitle(row.log.actor)}
            </Typography>
            <Typography variant="caption" className={classes.actorSecondary}>
              {row.log.actor?.type || '—'}
            </Typography>
          </Box>
        ),
      },
      {
        name: 'old_value',
        title: 'Old value',
        flex: 0.15,
        renderCell: (row: ChangeHistoryRow) => (
          <Typography
            variant="body2"
            className={classes.value}
            style={{ color: theme.palette.text.secondary }}>
            {getNullableValue(row.change?.old_value)}
          </Typography>
        ),
      },
      {
        name: 'new_value',
        title: 'Changed to',
        flex: 0.15,
        renderCell: (row: ChangeHistoryRow) => (
          <Typography variant="body2" className={classes.value}>
            {getNullableValue(row.change?.new_value)}
          </Typography>
        ),
      },
    ],
    [classes, theme.palette.text.secondary]
  );

  if (!rows.length) {
    return (
      <Box className={classes.emptyContainer} bgcolor={theme.palette.background.paper}>
        <Typography variant="subtitle2">No changes yet</Typography>
        <Typography variant="caption" color="textSecondary">
          When a change is made, it will appear here.
        </Typography>
      </Box>
    );
  }

  return <AppCustomTable columns={columns} data={rows} />;
};
